/**
 * useGraphRefresh — Reusable hook for event-driven graph refresh.
 *
 * Extracted from SpectatorWorkspace's proven mutation detection + polling loop.
 * Owns: mutation tracking, 2s polling, auto-project-selection from events.
 * Does NOT own: data fetching, WebSocket connection, or rendering.
 */
import { useEffect, useRef, useCallback, useState } from 'react'
import type { ToolCallEvent } from './useWorkspaceSocket'

const MUTATION_TOOLS = new Set([
  'create_activity',
  'update_activity',
  'delete_activity',
  'create_custom_exchange',
  'create_database_exchange',
  'update_exchange',
  'delete_exchange',
  'update_project',
])

function extractProjectIdFromMutation(ev: ToolCallEvent): string | null {
  if (ev.data.project_id) return ev.data.project_id
  return (ev.data.params?.project_id as string) || null
}

function extractProjectFromCreate(ev: ToolCallEvent): { id: string; name: string } | null {
  const preview = ev.data.result_preview as Record<string, unknown> | undefined
  const id = preview?.id as string | undefined
  const name = (preview?.project_name || ev.data.params?.name) as string | undefined
  if (id) return { id, name: name || 'Unnamed project' }
  return null
}

interface UseGraphRefreshOptions {
  events: ToolCallEvent[]
  refreshProject: () => Promise<void>
  loadProject: (projectId: string) => Promise<void>
  onProjectAutoSelected?: (projectId: string) => void
}

interface UseGraphRefreshReturn {
  selectedProjectId: string | null
  handleProjectSelect: (projectId: string) => void
}

export function useGraphRefresh({
  events,
  refreshProject,
  loadProject,
  onProjectAutoSelected,
}: UseGraphRefreshOptions): UseGraphRefreshReturn {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const selectedProjectIdRef = useRef<string | null>(null)
  const processedCallIdsRef = useRef<Set<string>>(new Set())
  const initializedRef = useRef(false)

  // Mutation counter + polling refs
  const mutationCountRef = useRef(0)
  const lastFetchedAtRef = useRef(0)
  const refreshingRef = useRef(false)
  const refreshProjectRef = useRef(refreshProject)
  refreshProjectRef.current = refreshProject
  const loadProjectRef = useRef(loadProject)
  loadProjectRef.current = loadProject
  const onProjectAutoSelectedRef = useRef(onProjectAutoSelected)
  onProjectAutoSelectedRef.current = onProjectAutoSelected

  const markDirty = useCallback(() => {
    mutationCountRef.current += 1
  }, [])

  const selectProject = useCallback((projectId: string) => {
    setSelectedProjectId(projectId)
    selectedProjectIdRef.current = projectId
    loadProjectRef.current(projectId)
    onProjectAutoSelectedRef.current?.(projectId)
    mutationCountRef.current = 0
    lastFetchedAtRef.current = 0
  }, [])

  // Polling loop: check every 2s if mutations occurred since last fetch
  useEffect(() => {
    const interval = setInterval(async () => {
      if (refreshingRef.current || !selectedProjectIdRef.current) return
      if (mutationCountRef.current === lastFetchedAtRef.current) return
      refreshingRef.current = true
      try {
        while (mutationCountRef.current !== lastFetchedAtRef.current) {
          lastFetchedAtRef.current = mutationCountRef.current
          await refreshProjectRef.current()
        }
      } finally {
        refreshingRef.current = false
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  // Event processing: detect mutations, auto-select projects
  useEffect(() => {
    if (events.length === 0) return

    // On first run, seed processedCallIds with existing events
    if (!initializedRef.current) {
      initializedRef.current = true
      let latestProjectId: string | null = null

      for (const ev of events) {
        if (ev.call_id && ev.data.status === 'completed') {
          processedCallIdsRef.current.add(ev.call_id)

          if (ev.data.tool_name === 'create_project') {
            const p = extractProjectFromCreate(ev)
            if (p) latestProjectId = p.id
          }
          if (MUTATION_TOOLS.has(ev.data.tool_name)) {
            const pid = extractProjectIdFromMutation(ev)
            if (pid) latestProjectId = pid
          }
        }
      }

      // Auto-select the most recent project if none selected
      if (latestProjectId && !selectedProjectIdRef.current) {
        selectProject(latestProjectId)
      }
      return
    }

    // Process new completed events
    for (const ev of events) {
      if (ev.data.status !== 'completed' || !ev.call_id) continue
      if (processedCallIdsRef.current.has(ev.call_id)) continue
      processedCallIdsRef.current.add(ev.call_id)

      // Auto-select on create_project if no project selected
      if (ev.data.tool_name === 'create_project') {
        const p = extractProjectFromCreate(ev)
        if (p && !selectedProjectIdRef.current) {
          selectProject(p.id)
        }
        continue
      }

      // Mark dirty if mutation targets current project
      if (MUTATION_TOOLS.has(ev.data.tool_name)) {
        const mutationPid = extractProjectIdFromMutation(ev)
        if (!mutationPid || mutationPid === selectedProjectIdRef.current) {
          markDirty()
        }
      }
    }

    // Cap the Set size
    if (processedCallIdsRef.current.size > 200) {
      const arr = [...processedCallIdsRef.current]
      processedCallIdsRef.current = new Set(arr.slice(-100))
    }
  }, [events, markDirty, selectProject])

  // Manual project selection
  const handleProjectSelect = useCallback((projectId: string) => {
    setSelectedProjectId(projectId)
    selectedProjectIdRef.current = projectId
    loadProjectRef.current(projectId)
    mutationCountRef.current = 0
    lastFetchedAtRef.current = 0
  }, [])

  return { selectedProjectId, handleProjectSelect }
}
